"use client"

import { useEffect, useRef, type MouseEvent, type ReactNode } from "react"

import { useLocale, useTranslations } from "next-intl"

import { Button, Heading, MenuChevronIcon, Stars, Surface, Text } from "@/components/ui"
import type { StoreProductReview, StoreProductReviews, ProductReviewQuery } from "@/platform/api/store-contract"

interface PdpReviewSummaryProps {
  summary: StoreProductReviews["summary"]
  renderedAt: number
  items?: StoreProductReview[]
  className?: string
  sort?: ReviewSortKey
  rating?: number
  hasMore?: boolean
  loading?: boolean
  loadError?: boolean
  onSortChange?: (sort: ReviewSortKey) => void
  onFilterChange?: (rating: number) => void
  onLoadMore?: () => Promise<boolean>
  onRetry?: () => Promise<boolean>
  pending?: boolean
}

export const REVIEWS_ANCHOR = "resenas"

export const jumpToReviews = (event: MouseEvent<HTMLAnchorElement>) => {
  const target = document.getElementById(REVIEWS_ANCHOR)
  if (!target) return
  event.preventDefault()
  window.history.pushState(null, "", `#${REVIEWS_ANCHOR}`)
  target.scrollIntoView()
  target.focus({ preventScroll: true })
}

const RATINGS = [5, 4, 3, 2, 1]

const RELATIVE_UNITS = [
  { unit: "year", ms: 365 * 24 * 60 * 60 * 1000 },
  { unit: "month", ms: 30 * 24 * 60 * 60 * 1000 },
  { unit: "week", ms: 7 * 24 * 60 * 60 * 1000 },
  { unit: "day", ms: 24 * 60 * 60 * 1000 },
] as const

type PdpT = ReturnType<typeof useTranslations<"pdp">>

export type ReviewSortKey = NonNullable<ProductReviewQuery["sort"]>

const timeAgo = (iso: string, renderedAt: number, locale: string, t: PdpT): string | null => {
  const elapsed = renderedAt - new Date(iso).getTime()
  if (Number.isNaN(elapsed) || elapsed < 0) return null
  const match = RELATIVE_UNITS.find(({ ms }) => elapsed >= ms)
  if (!match) return t("reviews.today")
  const phrase = new Intl.RelativeTimeFormat(locale, { numeric: "always" }).format(-Math.floor(elapsed / match.ms), match.unit)
  return phrase.charAt(0).toLocaleUpperCase(locale) + phrase.slice(1)
}

interface ReviewChipProps {
  value: string
  ariaLabel: string
  onChange: (value: string) => void
  children: ReactNode
}

const ReviewChip = ({ value, ariaLabel, onChange, children }: ReviewChipProps) => (
  <span className="ring-line rounded-10x relative inline-flex items-center ring-1 ring-inset">
    <select
      value={value}
      aria-label={ariaLabel}
      onChange={(event) => onChange(event.target.value)}
      className="font-text text-little @3xl:text-little-lg text-fg-heading focus-visible:ring-brand rounded-10x cursor-pointer appearance-none bg-transparent py-275 pr-900 pl-400 font-normal focus-visible:ring-2 focus-visible:outline-none @3xl:tracking-normal pointer-coarse:-my-50 pointer-coarse:py-325"
    >
      {children}
    </select>
    <span aria-hidden className="text-line @3xl:text-fg-heading pointer-events-none absolute right-400 flex h-1.5 w-2.75 items-center justify-center">
      <MenuChevronIcon className="shrink-0 rotate-90" />
    </span>
  </span>
)

export const PdpReviewSummary = ({
  summary,
  renderedAt,
  items = [],
  className,
  sort = "HIGHEST",
  rating = 0,
  hasMore = false,
  loading = false,
  loadError = false,
  onSortChange,
  onFilterChange,
  onLoadMore,
  onRetry,
  pending = false,
}: PdpReviewSummaryProps) => {
  const t = useTranslations("pdp")
  const locale = useLocale()
  const sectionRef = useRef<HTMLDivElement>(null)
  const pendingFocus = useRef<{ index: number; sort: ReviewSortKey; rating: number } | null>(null)
  useEffect(() => {
    const pending = pendingFocus.current
    if (pending === null) return
    if (pending.sort !== sort || pending.rating !== rating) {
      pendingFocus.current = null
      return
    }
    if (loading || loadError) return
    pendingFocus.current = null
    const reviews = sectionRef.current?.querySelectorAll<HTMLElement>("[data-review]")
    const target = reviews?.[pending.index] ?? reviews?.[0] ?? sectionRef.current
    target?.focus()
  }, [items, rating, sort, loading, loadError])
  const loadAndFocus = async (load: () => Promise<boolean>) => {
    const pending = { index: items.length, sort, rating }
    pendingFocus.current = pending
    if (!(await load()) && pendingFocus.current === pending) pendingFocus.current = null
  }
  const buckets = summary.rating_distribution
  const bucketed = buckets.reduce((total, bucket) => total + bucket.count, 0)
  const filterable = RATINGS.filter((rating) => buckets.some((bucket) => bucket.rating === rating && bucket.count > 0))
  return (
    <div ref={sectionRef} id={REVIEWS_ANCHOR} tabIndex={-1} className={`scroll-mt-sticky-page flex flex-col gap-975 outline-none @3xl:gap-750 ${className ?? ""}`}>
      <Heading as={pending ? "p" : "h2"} variant="sectionHead" family="text" tone="inherit" className="text-fg-heading">
        {pending ? t("reviews.headingPending") : t("reviews.heading", { count: summary.comment_count })}
      </Heading>
      <div className="@3xl:grid-cols-reviews flex flex-col gap-500 @3xl:grid @3xl:items-start @3xl:gap-y-0">
        <Surface variant="panel" radius="none" padding="none" className="rounded-8x @3xl:top-sticky-page @3xl:rounded-10x flex h-fit flex-col p-500 @3xl:sticky">
          <div className="flex items-start gap-150">
            <Text as="div" family="text" tone="brand" numeric className="text-product-title-lg">
              {summary.average_rating?.toFixed(1) ?? "—"}
            </Text>
            <Stars value={summary.average_rating ?? 0} size={18} className="text-fg-brand mt-325" />
          </div>
          <Text as="div" variant="bodySm" family="text" tone="inherit" className="text-fg-heading leading-detail">
            {t("reviews.count", { count: summary.rating_count })}
          </Text>
          {bucketed > 0 && (
            <div className="flex flex-col gap-50 pt-250 @3xl:gap-0 @3xl:pt-375">
              {buckets.map(({ rating, count: bucketCount }) => (
                <Text
                  key={rating}
                  as="div"
                  variant="captionLg"
                  family="text"
                  tone="brand"
                  numeric
                  className="leading-detail @3xl:text-body-sm flex h-4 items-center gap-125 font-semibold @3xl:h-4.5"
                >
                  <span className="w-2 shrink-0">{rating}</span>
                  <span className="bg-line-faint relative h-1.5 min-w-0 flex-1 overflow-hidden rounded-full">
                    <span className="bg-brand absolute inset-y-0 left-0 rounded-full" style={{ width: `${(bucketCount / bucketed) * 100}%` }} />
                  </span>
                </Text>
              ))}
            </div>
          )}
        </Surface>
        <div className="flex flex-col gap-375 @3xl:gap-500">
          {summary.comment_count > 1 && onSortChange && (
            <div className="flex flex-wrap gap-125 @3xl:gap-250">
              <ReviewChip
                value={sort}
                ariaLabel={t("reviews.sortLabel")}
                onChange={(next) => {
                  onSortChange(next as ReviewSortKey)
                }}
              >
                <option value="RECENT">{t("reviews.sortRecent")}</option>
                <option value="HIGHEST">{t("reviews.sortHighest")}</option>
                <option value="LOWEST">{t("reviews.sortLowest")}</option>
              </ReviewChip>
              {filterable.length > 1 && onFilterChange && (
                <ReviewChip
                  value={String(rating)}
                  ariaLabel={t("reviews.ratingLabel")}
                  onChange={(next) => {
                    onFilterChange(Number(next))
                  }}
                >
                  <option value="0">{t("reviews.ratingLabel")}</option>
                  {filterable.map((rating) => (
                    <option key={rating} value={rating}>
                      {t("reviews.ratingOption", { stars: rating })}
                    </option>
                  ))}
                </ReviewChip>
              )}
            </div>
          )}
          {(items.length > 0 || loading || loadError || rating > 0) && (
            <div className="flex flex-col gap-250">
              {items.map((review) => {
                const posted = timeAgo(review.submitted_at, renderedAt, locale, t)
                const author = review.author?.trim() || t("reviews.anonymousAuthor")
                const body = review.body?.trim()
                return (
                  <Surface
                    key={review.id}
                    variant="panel"
                    radius="none"
                    padding="none"
                    data-review
                    tabIndex={-1}
                    className="focus-visible:ring-brand rounded-10x flex flex-col gap-250 p-375 focus-visible:ring-2 focus-visible:outline-none @3xl:gap-375 @3xl:p-500"
                  >
                    <div className="flex items-start gap-250">
                      <Text
                        as="span"
                        family="text"
                        tone="inverted"
                        className="text-avatar from-brand-deep to-brand @3xl:text-product-title flex size-10 shrink-0 items-center justify-center rounded-full bg-linear-to-b uppercase @3xl:size-12.5"
                      >
                        {author.charAt(0)}
                      </Text>
                      <div className="flex min-w-0 flex-1 flex-col gap-125 @3xl:gap-75">
                        <Text as="b" family="text" tone="inherit" className="text-copy @3xl:text-copy-lg text-fg-heading truncate font-medium">
                          {author}
                        </Text>
                        <Stars value={review.rating} size={14} className="text-fg-brand" />
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-125">
                        {posted && (
                          <Text as="span" variant="captionLg" family="text" tone="inherit" className="text-fg-heading leading-detail @3xl:text-body-sm @3xl:leading-detail">
                            {posted}
                          </Text>
                        )}
                        {/* Figma's desktop card lets the pill run 5px into its right padding. */}
                        {review.verified_purchase && (
                          <Text
                            as="span"
                            variant="bodySm"
                            family="text"
                            tone="inherit"
                            className="text-fg-freeship border-fg-freeship inline-flex h-4.75 w-fit items-center rounded-full border px-150 leading-none @3xl:translate-x-1.25 @3xl:px-200"
                          >
                            {t("reviews.verifiedPurchase")}
                          </Text>
                        )}
                      </div>
                    </div>
                    {review.title?.trim() && (
                      <Text as="b" family="text" tone="inherit" className="text-copy text-fg-heading">
                        {review.title.trim()}
                      </Text>
                    )}
                    {body && (
                      <Text as="p" family="text" tone="inherit" className="text-copy @3xl:text-copy-lg text-fg-heading">
                        {body}
                      </Text>
                    )}
                  </Surface>
                )
              })}
              {items.length === 0 && rating > 0 && !loading && !loadError && (
                <Text as="div" variant="body" family="text" tone="muted" className="py-300">
                  {t("reviews.emptyFiltered", { stars: rating })}
                </Text>
              )}
              {loading && (
                <Text as="div" variant="body" tone="muted" role="status">
                  {t("reviews.loading")}
                </Text>
              )}
              {loadError && (
                <div role="alert">
                  <Text as="p" variant="body" tone="muted">
                    {t("reviews.loadFailed")}
                  </Text>
                  {onRetry && (
                    <Button variant="quiet" onClick={() => void loadAndFocus(onRetry)} disabled={loading}>
                      {t("reviews.retry")}
                    </Button>
                  )}
                </div>
              )}
              {hasMore && onLoadMore && (
                <Button
                  variant="quiet"
                  disabled={loading}
                  onClick={() => void loadAndFocus(onLoadMore)}
                  className="text-copy @3xl:text-copy-lg font-text text-fg-brand border-line-brand mt-125 self-center rounded-none border-b font-normal @3xl:mt-250 @3xl:self-start"
                >
                  {t("reviews.showMore")}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
